// Web Register cart — the ephemeral cart (mirrors Android RAM-only mCartList). Every mutation runs
// the pure pricing engine (recalculate) and stores derived totals. Order-level discounts are modeled
// as STACKED lines (`orderDiscounts`, Android's isSplitPaymentDiscount rows). Tax result Maps are NOT stored in
// Redux (non-serializable) — the checkout thunk recomputes them from the lines.

import { createSlice } from "@reduxjs/toolkit";
import {
  recalculate,
  recomputeLineDiscount,
  computeCouponEligibility,
  couponEligibleSubtotal,
  hasCouponExcludedItems,
  deziUpcSetOf,
  deziLineItemGrossSubtotal,
  deziEligibleGrossSubtotal,
  deziNetSubtotal,
  roundHalfUp,
  toNum,
  inflatePrice,
  dualActive,
  registerDisplay,
} from "../../../Components/WebRegister/engine";

let LINE_SEQ = 1;
const nextLineId = () => LINE_SEQ++;

const SHOW_OFFER_DOLLAR = "1";
const SHOW_OFFER_PERCENTAGE = "0";

const initialState = {
  lines: [],
  bogoList: [],
  // exclude_coupons_store: ids of register categories whose products are excluded from ORDER-level
  // list coupons (per-line isCouponEligible). Fed from the synced category list, like bogoList.
  couponExcludedCategoryIds: [],
  // QV-7124 — the last resolved mKonnekt get_discounts result. The pricing engine RE-DERIVES the
  // manufacturer discount from this on every recalculation, so it is the single source of truth and
  // applying it twice is impossible. null on a non-dezi store ⇒ the whole feature is inert.
  deziDiscounts: null,
  // Order-level discounts — a faithful port of Android's `isSplitPaymentDiscount` cart lines: each
  // applied discount is ONE entry, and they STACK exactly as the POS allows (any number of $ lines,
  // at most ONE % line, at most ONE list coupon). { name, amount, isPercent, isCoupon, isOtd, id,
  // altriaFunded }. The five fields below are DERIVED from this list by syncOrderDiscountFields() so
  // every downstream consumer (engine, payload, receipts, held sales) keeps reading the same shape.
  orderDiscounts: [],
  // Android `mOrderDiscount`: the model the % line is re-derived from on every cart change — a list
  // coupon, the OTD pseudo-coupon, or the synthetic "Discount" model a custom % writes. A custom $
  // never touches it (Android addSplitPaymentDiscountFlow). isListCoupon marks a real coupon.
  orderCouponModel: null,
  orderPercentDiscount: 0, // $ value of the (single) % line — Android penny orderPercentageDiscount
  orderAmountDiscount: 0, // Σ of the $ lines — Android penny orderAmountDiscount
  couponTotal: 0, // Σ of coupon-named lines (list coupons + OTD)
  customTotal: 0, // Σ of custom "Discount" lines
  coupon: null, // { code, id, altriaFunded } of the applied LIST coupon line (Android getLoyaltyJson couponName)
  isEighteenPlus: false, // 21+/age verified for this order (tobacco)
  idNumber: "",
  idCardType: "",
  idDob: "", // Android idCardDateOfBirth → save `dob` ("yyyy-MM-dd HH:mm:ss")
  idCardExDate: "", // Android idCardExpiryDate → save `id_card_ex_date`
  isOutdoor: false,
  outDoorPrice: 0,
  customer: null,
  // Order-level reductions (pay down the grand total): gift card(s) / loyalty / store credit.
  giftCards: [], // [{ number, amountUsed, balance }]
  loyaltyAmount: 0,
  loyaltyPointsSpent: 0,
  storeCreditAmount: 0,
  // Payment tenders paying the reduced balance (cash / card / EBT). 1 = one-shot, 2+ = split.
  payments: [], // [{ payType, amount, tip, paymentId, cardType, last4, paxDetails, paymentResult, cvvResult }]
  // Order id of the payment in progress. Lives on the cart (not the modal) so closing/reopening the
  // payment screen mid-split keeps ONE id — the active-order record and Payroc reconcile stay
  // consistent, and a completed order can never resurface as a phantom "unfinished" one.
  activeOrderId: null,
  // QV-4241 store surcharge mode (Android setNonCashAdjustment): { mode "0"|"1"|"2", pct, debitPct }.
  // Mode "2" prices every catalog/custom line at the CARD price (repriceLines); the cash side is derived.
  dualPrice: { mode: "0", pct: 0, debitPct: 0 },
  // Android cbNca / ncaRemoved (NCA mode "1" only): the cashier waived the surcharge for THIS order.
  // Lives on the cart so it survives a closed payment screen or an aborted terminal (b8d105432f).
  ncaRemoved: false,
  totals: { subtotal: 0, taxRateInAmount: 0, taxLines: [], lotteryBuyAmount: 0, grandTotal: 0, paid: 0, balanceDue: 0, customerOwes: 0, lotteryPayoutAmount: 0, scratcherPayoutAmount: 0 },
};

// ── Order-discount line helpers (Android mCartList.filter { isSplitPaymentDiscount } idioms) ──
const dLines = (state) => (Array.isArray(state.orderDiscounts) ? state.orderDiscounts : []);
const sumAmt = (arr) => roundHalfUp(arr.reduce((s, d) => s + toNum(d.amount), 0));
const percentLine = (state) => dLines(state).find((d) => d.isPercent) || null;
const isListCouponLine = (d) => !!d.isCoupon && !d.isOtd;

/** Re-derive the five legacy fields every consumer reads from the discount lines. */
function syncOrderDiscountFields(state) {
  const ls = dLines(state);
  const pct = percentLine(state);
  state.orderPercentDiscount = pct ? roundHalfUp(toNum(pct.amount)) : 0; // hasManyDecimal().roundOffDecimal()
  state.orderAmountDiscount = sumAmt(ls.filter((d) => !d.isPercent));
  state.couponTotal = sumAmt(ls.filter((d) => d.isCoupon));
  state.customTotal = sumAmt(ls.filter((d) => !d.isCoupon));
  const lc = ls.find(isListCouponLine);
  state.coupon = lc ? { code: lc.name, id: lc.id || "", altriaFunded: lc.altriaFunded || "" } : null;
}

/** Android deleteCoupon() + removeSplitDiscount(true): every order-level discount goes. */
function clearAllOrderDiscounts(state) {
  state.orderDiscounts = [];
  state.orderCouponModel = null;
  state.isOutdoor = false;
  state.outDoorPrice = 0;
  syncOrderDiscountFields(state);
}

// Android isSplitPayment(): a tender that did NOT settle the whole order is on the books.
const isSplitState = (state) => {
  const ps = state.payments || [];
  return ps.length > 1 || (ps.length === 1 && !!ps[0].partial);
};

// Dual price (Android DatabaseRepository reprice + inflateCustomItemPrice): every catalog / custom line
// carries its server/typed `basePrice`; its card price is derived here so a mode or % change reprices
// an open cart. Sold gift cards and payout lines are never inflated. A typed price override is a CASH
// price (manualOverrideBase) inflated the same way (Android IPF:8806-8814).
function inflateCsv(cfg, csv) {
  if (csv == null || csv === "") return csv;
  return String(csv)
    .split(",")
    .map((v) => (String(v).trim() === "" ? v : String(inflatePrice(cfg, v))))
    .join(",");
}

function repriceLines(state) {
  const cfg = state.dualPrice;
  const active = dualActive(cfg);
  for (const l of state.lines) {
    if (l.isSplitPaymentDiscount || l.isLotteryPayout || l.isScratcherPayout) continue;
    if (l.basePrice == null) l.basePrice = toNum(l.catalogUnitPrice);
    if (l.isGiftCard) {
      l.catalogUnitPrice = toNum(l.basePrice);
      continue;
    }
    l.catalogUnitPrice = inflatePrice(cfg, l.basePrice);
    if (toNum(l.manualOverrideBase) > 0) l.manualOverridePrice = inflatePrice(cfg, l.manualOverrideBase);
    // The line model can be a shared/frozen object — replace it, never mutate it. Only touched while
    // dual is active (or to restore after it was), so every other store keeps its model untouched.
    const m = l.model;
    if (!m || typeof m !== "object") continue;
    if (active) {
      const baseP = m.basePrice != null ? m.basePrice : m.price;
      const baseV = m.baseVPrice != null ? m.baseVPrice : m.vPrice;
      l.model = {
        ...m,
        basePrice: baseP,
        baseVPrice: baseV,
        price: baseP != null && baseP !== "" ? String(inflatePrice(cfg, baseP)) : baseP,
        vPrice: inflateCsv(cfg, baseV),
      };
    } else if (m.basePrice != null || m.baseVPrice != null) {
      const { basePrice, baseVPrice, ...rest } = m;
      l.model = { ...rest, price: basePrice != null ? basePrice : m.price, vPrice: baseVPrice != null ? baseVPrice : m.vPrice };
    }
  }
}

/**
 * The ONE place the engine inputs are derived from cart state — used by every cart recalculation AND
 * by submitOrder's payload recalculation, so the saved order can never diverge from what was charged
 * (it used to drop the dezi result, the OTD flag and the coupon split).
 */
/**
 * The price to hand back to `overridePrice` when the line editor is CANCELLED. `overridePrice` treats
 * its input as a CASH price (dual price inflates it), so the restore must use the snapshot's cash
 * override — re-sending the card-priced manualOverridePrice would inflate it a second time.
 */
export function overrideRestorePrice(snapshot) {
  if (!snapshot) return 0;
  return snapshot.manualOverrideBase != null ? toNum(snapshot.manualOverrideBase) : toNum(snapshot.manualOverridePrice);
}

export function buildRecalcInput(state, cart, giftCard) {
  const pct = percentLine(state);
  const gc = giftCard != null ? giftCard : (state.giftCards || []).reduce((s, g) => s + toNum(g.amountUsed), 0);
  return {
    cart,
    bogoList: state.bogoList || [],
    now: new Date(),
    orderPercentDiscount: state.orderPercentDiscount,
    orderAmountDiscount: state.orderAmountDiscount,
    couponTotal: state.couponTotal,
    customTotal: state.customTotal,
    // Android penny: the list-coupon part of the $ discount (spread over eligible lines only) vs the
    // custom/OTD part (full-cart spread), and whether the % line is a REAL list coupon.
    couponDollarDiscount: state.isOutdoor ? 0 : sumAmt(dLines(state).filter((d) => !d.isPercent && isListCouponLine(d))),
    percentIsRealCoupon: !!(pct && isListCouponLine(pct)),
    loyaltyAmount: state.loyaltyAmount,
    storeCredit: state.storeCreditAmount,
    giftCard: gc,
    isOtdCoupon: !!state.isOutdoor, // OTD keeps the full-cart coupon spread (exclude_coupons_store)
    deziDiscounts: state.deziDiscounts || null,
    nextLineId,
    dualPrice: state.dualPrice || null,
    isSplit: isSplitState(state),
  };
}

function runRecalc(state, giftCard) {
  return recalculate(buildRecalcInput(state, state.lines, giftCard));
}

/** The upcs carrying a live manufacturer discount, derived from the held result. */
function deziSet(state) {
  return deziUpcSetOf(state.deziDiscounts);
}

// exclude_coupons_store: base a REAL list coupon computes against — eligible-only subtotal when the
// cart has excluded items, else the legacy subtotal − lottery buys. (Custom discounts + OTD always
// keep the full base — Android getDiscountAmount.)
//
// QV-7124 rule 4: the manufacturer-discounted portion is then REMOVED, so a coupon can never be
// funded by — nor have its MINIMUM met by — money the manufacturer is paying for. The gate and the
// computation deliberately read the SAME expression: Android's bug was exactly that they disagreed,
// so a coupon passed the gate, computed $0.00 and stranded itself as "already applied".
function listCouponBase(state, subtotal, lotteryBuyAmount) {
  const upcs = deziSet(state);
  const excluded = hasCouponExcludedItems(state.lines);
  const base = excluded ? couponEligibleSubtotal(state.lines) : subtotal - (lotteryBuyAmount || 0);
  return Math.max(base - deziNetSubtotal(state.lines, upcs, excluded), 0);
}

// Android OrderCreateDataModel.isCustomItem(i): every unit MADE from a custom line (add with a
// multiplier, cart "+" stepper, editor qty) is renamed `base + " " + (count + 1)` where base is the
// category title for a category-as-item line, else the line name with any trailing " <number>"
// removed, and count = lines already in the cart named base or "base …" — counted as the cart grows.
// So "+" on "Custom Item 1" yields "Custom Item 2", then "Custom Item 3", never a duplicate name.
function customUnitName(lines, line) {
  const m = line.model || {};
  const base = String(m.categoryName || "").trim() || String(line.orderName || "").trim().replace(/\s+\d+$/, "").trim() || "Custom Item";
  const cid = String(line.orderCategoryId || "").trim();
  let count;
  if (cid) {
    // category-as-item: same category id + custom-item flag (Android `model.id == 0`)
    count = lines.filter((x) => String(x.orderCategoryId || "").trim() === cid && isCustomUnit(x)).length;
  } else {
    const prefix = (base + " ").toLowerCase();
    count = lines.filter((x) => {
      const s = String(x.orderName || "").trim();
      return s === base || s.toLowerCase().startsWith(prefix);
    }).length;
  }
  return `${base} ${count + 1}`;
}
const isCustomUnit = (l) => !!l.isCustomLineItem || String(l.orderProductId) === "0";

// Gross (unit×count, pre-discount) over the product lines that satisfy `pred` — the sums Android's
// removeSplitDiscount over-discount / min-amount re-checks are built from.
function grossOf(lines, pred) {
  return lines
    .filter((l) => !l.isSplitPaymentDiscount && !l.isSplitPayment && !l.isSplitRefund && pred(l))
    .reduce((s, l) => {
      const unit = toNum(l.orderUnitNewPrice) > 0 ? toNum(l.orderUnitNewPrice) : toNum(l.orderUnitPrice);
      return s + unit * toNum(l.orderCount);
    }, 0);
}

/**
 * Android `getDiscountAmount()` — re-value the single % line against the CURRENT cart. Returns the
 * new $ value, or null when the coupon's minimum is no longer met (Android deleteCoupon()).
 *   custom %      : base = subtotal − Σ$ lines − lottery buys − dezi (all lines)
 *   real % coupon : base = excluded items ? eligible subtotal : eligible subtotal − Σ$ lines; − dezi (eligible)
 * ⚠️ Only NON-percentage lines are subtracted here (the % line never feeds its own base), while the
 * APPLY path subtracts every existing line — that asymmetry is Android's and is kept.
 */
function rederivePercentLine(state, r) {
  const model = state.orderCouponModel;
  if (!model) return null;
  const existingDollar = sumAmt(dLines(state).filter((d) => !d.isPercent));
  const isRealCoupon = !!model.isListCoupon && String(model.name).toUpperCase() !== "OTD";
  const upcs = deziSet(state);
  let base;
  if (!isRealCoupon) base = r.subtotal - existingDollar - (r.lotteryBuyAmount || 0);
  else if (hasCouponExcludedItems(state.lines)) base = couponEligibleSubtotal(state.lines);
  else base = couponEligibleSubtotal(state.lines) - existingDollar;
  base = Math.max(base - deziNetSubtotal(state.lines, upcs, isRealCoupon), 0);
  if (toNum(model.minAmount) > base) return null;
  let val;
  if (model.flag === SHOW_OFFER_DOLLAR) val = toNum(model.discount);
  else {
    val = roundHalfUp(base * (toNum(model.discount) / 100));
    const cap = toNum(model.maximumDiscount);
    if (cap !== 0 && val > cap) val = cap;
  }
  if (val > base) val = base;
  return roundHalfUp(val);
}

function recompute(state) {
  const giftCard = (state.giftCards || []).reduce((s, g) => s + toNum(g.amountUsed), 0);
  if (!Array.isArray(state.orderDiscounts)) state.orderDiscounts = [];
  // Android removeSplitDiscount `productSize == 0` → mCartList.clear() + resetOrder: with no product
  // left there is nothing for a discount to sit on, so every order-level discount goes with the cart.
  const productLines = state.lines.filter((l) => !l.isSplitPaymentDiscount && !l.isSplitPayment && !l.isSplitRefund);
  if (productLines.length === 0 && dLines(state).length > 0) clearAllOrderDiscounts(state);
  syncOrderDiscountFields(state);
  repriceLines(state);
  let r = runRecalc(state, giftCard);

  // Android splitDiscountUpdate: the % line is re-derived on EVERY cart change (write only on change).
  const pct = percentLine(state);
  if (pct) {
    const nv = rederivePercentLine(state, r);
    if (nv === null) {
      // getDiscountAmount → deleteCoupon(): the model is dropped and the line, now worth $0, is pruned.
      state.orderCouponModel = null;
      state.isOutdoor = false;
      state.outDoorPrice = 0;
      pct.amount = 0;
      syncOrderDiscountFields(state);
      r = runRecalc(state, giftCard);
    } else if (roundHalfUp(nv) !== roundHalfUp(toNum(pct.amount))) {
      pct.amount = nv;
      syncOrderDiscountFields(state);
      r = runRecalc(state, giftCard);
    }
  }

  // OTD exact-total reconciliation (Android calculateOrder back-correction): after tax, nudge the
  // OTD $-coupon by the residual so the grand total lands EXACTLY on the target, then recompute. One
  // correction (like Android) — the cart can't change while OTD is active, so it converges.
  if (state.isOutdoor && toNum(state.outDoorPrice) > 0) {
    const otd = dLines(state).find((d) => d.isOtd);
    const residual = roundHalfUp(toNum(state.outDoorPrice) - r.grandTotal);
    if (!otd) {
      // Android: the OTD line is gone but isOutdoor is still true → tear OTD down (else the register wedged).
      clearAllOrderDiscounts(state);
      r = runRecalc(state, giftCard);
    } else if (residual !== 0) {
      const adj = roundHalfUp(toNum(state.couponTotal) - residual);
      if (adj >= 0) {
        otd.amount = adj;
        syncOrderDiscountFields(state);
        r = runRecalc(state, giftCard);
      } else {
        // Android calculateOrder: if the adjustment would drive the coupon negative, clear the whole OTD.
        clearAllOrderDiscounts(state);
        r = runRecalc(state, giftCard);
      }
    }
  }

  // Android removeSplitDiscount (runs after every cart change):
  //   totalDiscount > (gross − lottery buys − dezi gross)  OR  mOrderDiscount.minAmount > eligible gross
  //     ⇒ drop EVERY discount line + mOrderDiscount   ($20+$30, apply $40, delete the $30 ⇒ 40 > 20 ⇒ gone)
  //   else ⇒ prune only the lines whose value is $0.
  // A `$` line is written ONCE at apply and never re-valued, so this is its only escape when the cart
  // shrinks. The nets are dezi-aware (QV-7124 rule 4) — a dezi line can never absorb an order discount.
  if (dLines(state).length > 0) {
    const upcs = deziSet(state);
    const totalDiscountSum = sumAmt(dLines(state));
    const lotteryGross = grossOf(state.lines, (l) => l.isLotteryTicket === "1");
    const totalLineItemSum = roundHalfUp(grossOf(state.lines, () => true) - lotteryGross - deziLineItemGrossSubtotal(state.lines, upcs));
    const eligibleLineItemSum = roundHalfUp(
      Math.max(grossOf(state.lines, (l) => l.isCouponEligible !== false && l.isLotteryTicket !== "1") - deziEligibleGrossSubtotal(state.lines, upcs), 0)
    );
    const model = state.orderCouponModel;
    let changed = false;
    if (totalDiscountSum > totalLineItemSum || (model && toNum(model.minAmount) > eligibleLineItemSum)) {
      clearAllOrderDiscounts(state);
      changed = true;
    } else {
      const kept = dLines(state).filter((d) => roundHalfUp(toNum(d.amount)) !== 0);
      if (kept.length !== dLines(state).length) {
        state.orderDiscounts = kept;
        syncOrderDiscountFields(state);
        changed = true;
      }
    }
    if (changed) r = runRecalc(state, giftCard);
  }

  // Android parity: BOGO rows sink to the bottom of the cart (mCartList.sortedBy { isBoGoProduct }).
  state.lines = [...state.lines].sort((a, b) => (a.isBoGoProduct ? 1 : 0) - (b.isBoGoProduct ? 1 : 0));
  const paid = (state.payments || []).reduce((s, p) => s + toNum(p.amount), 0);
  // Dual price: tenders pay down the CASH (base) total — each card leg records its base + its NCA
  // premium (Android getRemainingSplitBaseAmount = baseGrandTotal − Σ stored base legs).
  const dual = r.dual && r.dual.dual ? r.dual : null;
  // A wallet that covered more than the cash total (Android's gift-card cap is the CARD total) must not
  // read as a payout — only real lottery/scratcher payouts may drive what's owed below zero.
  const owedTotal = dual ? (toNum(r.lotteryScratcher) > 0 ? dual.baseGrandTotal : Math.max(dual.baseGrandTotal, 0)) : r.grandTotal;
  const balanceDue = Math.max(roundHalfUp(owedTotal - paid), 0);
  // Android customerOwes = mGrandTotal − totalSplitAmount, UNCLAMPED: negative when a lottery /
  // scratcher payout exceeds the sale (the register then pays the customer in cash).
  const customerOwes = roundHalfUp(owedTotal - paid);
  // Serializable per-tax breakdown for the cart panel (mirrors Android's per-tax rows).
  const taxByName = r.tax && r.tax.taxByName instanceof Map ? r.tax.taxByName : new Map();
  const taxLines = Array.from(taxByName.values())
    .filter((tx) => roundHalfUp(tx.price) > 0 || toNum(tx.percent) > 0)
    .map((tx) => ({ name: tx.name, percent: toNum(tx.percent), percentRaw: tx.percentRaw || "", amount: roundHalfUp(tx.price) }));
  state.totals = {
    subtotal: r.subtotal,
    taxRateInAmount: r.taxRateInAmount,
    taxLines,
    foodStampable: r.totalFoodStampable || 0, // Android tvFoodStampable — EBT-eligible total incl. tax
    lotteryBuyAmount: r.lotteryBuyAmount || 0, // OTD back-solve excludes lottery buys from the base
    grandTotal: r.grandTotal, // net of reductions
    paid: roundHalfUp(paid),
    balanceDue,
    customerOwes,
    lotteryPayoutAmount: roundHalfUp(r.lotteryPayoutAmount || 0),
    scratcherPayoutAmount: roundHalfUp(r.scratcherPayoutAmount || 0),
    // QV-4241 cash/card columns (null unless the store is in dual-price mode). grandTotal stays the
    // credit-space mGrandTotal; balanceDue/customerOwes are in CASH space when this is set.
    dual: dual ? { ...dual } : null,
  };
  // What the register shows (Android: card columns in dual, remaining amount on the Pay button mid-split).
  state.totals.display = registerDisplay(state.dualPrice, {
    subtotal: r.subtotal,
    orderDiscount: roundHalfUp(toNum(state.couponTotal) + toNum(state.customTotal)),
    taxLines: state.totals.taxLines.map((t) => ({ ...t })),
    grandTotal: r.grandTotal,
    customerOwes,
    isSplit: isSplitState(state),
    dual,
  });
}

function makeLine(p) {
  const unit = toNum(p.unitPrice);
  return {
    orderId: nextLineId(),
    orderProductId: String(p.productId),
    // Android orderCategoryId — the product's category CSV, or the category id of a category-as-item
    // line (isCustomItem(i) counts those by this key).
    orderCategoryId: String(p.categoryId ?? (p.model || {}).cotegory ?? ""),
    orderName: p.name || "",
    orderCount: toNum(p.qty) || 1,
    isVariant: !!p.isVariant,
    variantId: p.variantId || "",
    // The chosen variant's OWN scan code. `model.vupc` is the comma-joined catalog string for ALL
    // variants, so it can never identify one line — dezi matches on this, and item_ses sends it.
    variantUpc: p.variantUpc || "",
    variantMedia: p.variantMedia || "", // Android vMedia (7494508ccf) — the variant's own image filename
    basePrice: unit, // server / typed CASH unit price — catalogUnitPrice is derived (repriceLines)
    catalogUnitPrice: unit,
    orderUnitPrice: unit,
    orderUnitNewPrice: 0,
    manualOverridePrice: 0,
    orderDiscount: 0,
    orderDiscountPercent: 0,
    // QV-4738 — manual discount and the item-level coupon live SEPARATELY; orderDiscount above
    // carries the COMBINED per-unit total the engine reads (recomputeLineDiscount derives it).
    manualDiscountAmt: 0, // manual $ per unit (the "Discount" line)
    manualDiscountPercent: 0, // manual % (0 if entered as $)
    couponDiscountName: "", // item-coupon code; "" = no item coupon
    couponDiscountType: "", // "percentage" | "amount"
    couponDiscountValue: 0, // raw coupon value (20 for 20%, 5 for $5)
    couponDiscountFinalAmt: 0, // computed coupon $ per unit, on the post-manual-discount price
    couponMaxDiscount: 0, // % coupon Maximum Discount cap per unit (0 = no cap)
    couponCanCombine: false, // coupon's can_combine_discounts flag
    // QV Altria (Android 2026-08-20): the Altria-funded switch moved OFF the order-level coupon form
    // (which now always saves altria_funded_discount=0) onto SPECIFIC item coupons, so the flag is
    // captured per line at apply time and reported in item_ses.altria_discount_desc.coupon.
    couponAltriaFunded: "0", // item coupon's altria_funded_discount flag ("1"/"0")
    couponPromotionCode: "", // item coupon's Altria LFP promotion code; "" unless the coupon is funded
    // Category-derived loyalty eligibility (Android canUsePoint/canEarnPoint). The add sites pass
    // explicit booleans from categoryLoyaltyFlags; when absent (legacy/loadCart), a catalog line
    // defaults eligible and a custom line defaults ineligible.
    canUsePoint: p.canUsePoint != null ? !!p.canUsePoint : !p.isCustomLineItem,
    canEarnPoint: p.canEarnPoint != null ? !!p.canEarnPoint : !p.isCustomLineItem,
    isLotteryTicket: p.isLotteryTicket || "0",
    isLotteryPayout: false,
    isScratcherPayout: false,
    isBoGoProduct: false,
    bogoIdentifier: 0,
    bogoDealId: null,
    bogoDealName: "",
    bogoAltriaFunded: "",
    bogoPromotionCode: "",
    // Design 2 per-order lock: set when a fired set exhausts `bogo_limit_per_order`, so a later
    // same-deal add stays PAID instead of re-shuffling the set that already fired. Survives
    // recalculation on purpose (see engine/index.js resetWorkingPrices).
    isCommittedBogo: false,
    isRestoredBogo: false,
    isWarningShow: "", // the deal's promo hint ("Buy 1, Get 1 …"); display only
    isBulkPriceApplied: false,
    // QV-7124 — per-unit manufacturer discount + its Altria reporting breakdown. Both are DERIVED on
    // every recalculation from the held mKonnekt result, never accumulated.
    deziItemDiscountAmt: 0,
    deziBreakdown: [],
    isDeziUpcLine: false,
    isCustomLineItem: !!p.isCustomLineItem,
    // exclude_coupons_store: false when any of the product's categories is coupon-excluded — the
    // line then never receives an ORDER-level list-coupon share (item coupons are NOT gated).
    isCouponEligible: p.isCouponEligible !== false,
    stockTracked: !!p.stockTracked,
    maxStock: toNum(p.maxStock),
    purchaseQtyCap: toNum(p.purchaseQtyCap), // per-transaction limit; 0 = unlimited
    note: "",
    isGiftCard: !!p.isGiftCard,
    isSplitPaymentDiscount: false,
    isSplitPayment: false,
    isSplitRefund: false,
    jsonNewPrice: 0,
    jsonCouponCodeAmt1: 0,
    jsonCouponCodeAmt2: 0,
    jsonLoyaltyAmt: 0,
    customDiscountAmt: 0,
    couponDiscountAmt: 0,
    taxList: p.taxList || [],
    model: p.model || {},
  };
}

const cartSlice = createSlice({
  name: "webRegisterCart",
  initialState,
  reducers: {
    setBogoList(state, { payload }) {
      state.bogoList = payload || [];
      recompute(state);
    },
    // QV-4241 — store surcharge mode from register settings (dualPriceConfig). A change reprices the
    // open cart from each line's basePrice (Android reprices the product table on a mode change).
    setDualPrice(state, { payload }) {
      const next = {
        mode: String((payload && payload.mode) || "0"),
        pct: toNum(payload && payload.pct),
        debitPct: toNum(payload && payload.debitPct),
        debitSetting: toNum(payload && payload.debitSetting),
      };
      const cur = state.dualPrice || {};
      if (cur.mode === next.mode && toNum(cur.pct) === next.pct && toNum(cur.debitPct) === next.debitPct && toNum(cur.debitSetting) === next.debitSetting) return;
      state.dualPrice = next;
      recompute(state);
    },
    // QV-7124 — a get_discounts lookup resolved. Storing the RESULT (not the applied amounts) is what
    // makes the injection a pure function of the cart, so a hold→restore or a repeated recalculation
    // can never double-apply a manufacturer discount.
    setDeziDiscounts(state, { payload }) {
      state.deziDiscounts = payload || null;
      recompute(state);
    },
    // exclude_coupons_store: ids of categories flagged exclude-coupons (register list). Re-derives
    // every line's eligibility so a category-list refresh updates the open cart (Android keeps the
    // restore VM's category list in sync for exactly this reason).
    setCouponExcludedCategories(state, { payload }) {
      state.couponExcludedCategoryIds = (payload || []).map(String);
      for (const l of state.lines) {
        l.isCouponEligible = computeCouponEligibility((l.model || {}).cotegory, state.couponExcludedCategoryIds);
      }
      recompute(state);
    },
    // PER-UNIT ROWS (Android addProductWithQuantity): quantity N = N separate rows, each
    // orderCount=1 — never one row with qty N. BOGO discounts individual cheapest rows, Mix&Match
    // counts rows toward the threshold, the cart displays one line per unit, and item_ses emits one
    // entry per unit (sp_quty "1") — all exactly like the POS. validateMultipliers parity: no row
    // may ever carry orderCount>1.
    addItem(state, { payload }) {
      const pid = String(payload.productId);
      const vid = payload.variantId || "";
      const existingUnits = state.lines
        .filter((l) => l.orderProductId === pid && (l.variantId || "") === vid)
        .reduce((s, l) => s + toNum(l.orderCount), 0);
      // Stock cap + per-transaction purchase cap (safety net; the UI blocks first with a message).
      let allowed = toNum(payload.qty) || 1;
      if (payload.stockTracked && payload.maxStock > 0) allowed = Math.min(allowed, Math.max(payload.maxStock - existingUnits, 0));
      if (toNum(payload.purchaseQtyCap) > 0) allowed = Math.min(allowed, Math.max(toNum(payload.purchaseQtyCap) - existingUnits, 0));
      // exclude_coupons_store: eligibility from the product's categories at add time (Android
      // addProductWithQuantity). Custom items with no category stay eligible.
      const couponEligible = computeCouponEligibility(
        (payload.model || {}).cotegory ?? payload.categoryId,
        state.couponExcludedCategoryIds
      );
      for (let i = 0; i < allowed; i++) {
        const line = makeLine({ ...payload, qty: 1, isCouponEligible: couponEligible });
        state.lines.push(line);
      }
      recompute(state);
    },
    // qty semantics on a UNIT row: 0 → remove the row; N>1 → expand into N rows (deep copies of this
    // row's state, like Android's expandMultiplier) — so CartPanel's +/− add/remove sibling units.
    updateQty(state, { payload }) {
      const idx = state.lines.findIndex((x) => x.orderId === payload.orderId);
      if (idx < 0) return;
      const l = state.lines[idx];
      const q = toNum(payload.qty);
      if (q <= 0) {
        state.lines.splice(idx, 1);
      } else if (q > 1) {
        const pid = l.orderProductId;
        const vid = l.variantId || "";
        const units = state.lines
          .filter((x) => x.orderProductId === pid && (x.variantId || "") === vid)
          .reduce((s, x) => s + toNum(x.orderCount), 0);
        let extra = q - toNum(l.orderCount);
        if (l.stockTracked && l.maxStock > 0) extra = Math.min(extra, Math.max(l.maxStock - units, 0));
        if (toNum(l.purchaseQtyCap) > 0) extra = Math.min(extra, Math.max(toNum(l.purchaseQtyCap) - units, 0));
        for (let i = 0; i < extra; i++) {
          const copy = JSON.parse(JSON.stringify({ ...l, orderCount: 1 }));
          copy.orderId = nextLineId();
          if (isCustomUnit(l) && !l.isLotteryPayout && !l.isScratcherPayout) {
            // Android isCustomItem(i) + mCartList.add(): numbered AND appended, so the cart reads 1, 2, 3, 4.
            copy.orderName = customUnitName(state.lines, l);
            state.lines.push(copy);
          } else {
            state.lines.splice(idx + 1 + i, 0, copy);
          }
        }
      }
      recompute(state);
    },
    removeItem(state, { payload }) {
      state.lines = state.lines.filter((x) => x.orderId !== payload);
      recompute(state);
    },
    // Android setLotteryPayout / setScratcherPayout: a payout is a cart LINE — "Lottery Payout" /
    // "Scratcher Payout", amount in orderUnitNewPrice, qty 1, no tax, no points. The engine buckets
    // it (subtotal.js), excludes it from tax/penny/food-stampable and subtracts it in the grand total.
    // manualOverridePrice carries the amount too, because resetWorkingPrices re-seeds
    // orderUnitNewPrice from it on every recalculation.
    addPayoutLine(state, { payload }) {
      const amt = roundHalfUp(toNum(payload && payload.amount));
      if (amt <= 0) return;
      const scratcher = payload.kind === "scratcher";
      const l = makeLine({
        productId: "0",
        name: scratcher ? "Scratcher Payout" : "Lottery Payout",
        unitPrice: 0,
        qty: 1,
        taxList: [],
        model: {},
        canUsePoint: false,
        canEarnPoint: false,
      });
      l.orderUnitNewPrice = amt;
      l.manualOverridePrice = amt;
      l.isLotteryPayout = !scratcher;
      l.isScratcherPayout = scratcher;
      state.lines.push(l);
      recompute(state);
    },
    // Editor-Done "expand": Android's orderUpdate→expandMultiplier — the editor holds a preview
    // orderCount=N; on Done the line is exploded into N unit rows carrying its edited fields.
    expandLine(state, { payload }) {
      const idx = state.lines.findIndex((l) => l.orderId === payload.orderId);
      if (idx < 0) return;
      const n = Math.max(1, Math.floor(toNum(payload.count)));
      if (n <= 1) return;
      const base = state.lines[idx];
      const copies = [];
      for (let i = 1; i < n; i++) {
        const c = JSON.parse(JSON.stringify({ ...base, orderCount: 1 }));
        c.orderId = nextLineId();
        if (isCustomUnit(base)) c.orderName = customUnitName([...state.lines, ...copies], base); // Android isCustomItem(i)
        copies.push(c);
      }
      state.lines.splice(idx + 1, 0, ...copies);
      recompute(state);
    },
    // Manual item discount ONLY (QV-4738: the item coupon is separate and re-adjusts to the
    // post-manual price via recomputeLineDiscount). Clearing (0,0) keeps any coupon on the line —
    // Android ivDiscount removes just the manual part and the coupon recomputes to the full price.
    setItemDiscount(state, { payload }) {
      const l = state.lines.find((x) => x.orderId === payload.orderId);
      if (l) {
        l.manualDiscountAmt = toNum(payload.amount);
        l.manualDiscountPercent = toNum(payload.percent);
        recomputeLineDiscount(l);
        if (l.manualDiscountAmt > 0 || l.manualDiscountPercent > 0) {
          // Android: a manual discount kicks the line out of a BOGO deal + clears its promo warning.
          l.isBoGoProduct = false;
          l.bogoIdentifier = 1;
          l.isWarningShow = "";
        } else if (!l.couponDiscountName && toNum(l.bogoIdentifier) === 1) {
          l.bogoIdentifier = 0; // Android ivDiscount: clear the sentinel only when no coupon remains
        }
        recompute(state);
      }
    },
    // QV-4738 apply an item-level coupon to one line (validation runs in the modal, like Android's
    // applyItemCoupon does before storing). payload.coupon: { name, type "percentage"|"amount",
    // value, maxDiscount, canCombine, altriaFunded }.
    applyItemCoupon(state, { payload }) {
      const l = state.lines.find((x) => x.orderId === payload.orderId);
      if (!l) return;
      const c = payload.coupon || {};
      l.couponDiscountName = String(c.name || "");
      l.couponDiscountType = c.type === "amount" ? "amount" : "percentage";
      l.couponDiscountValue = toNum(c.value);
      l.couponMaxDiscount = toNum(c.maxDiscount);
      l.couponCanCombine = !!c.canCombine;
      l.couponAltriaFunded = String(c.altriaFunded || "") === "1" ? "1" : "0";
      l.couponPromotionCode = l.couponAltriaFunded === "1" ? String(c.promotionCode || "").trim() : "";
      l.isWarningShow = "";
      l.isBoGoProduct = false;
      l.bogoIdentifier = 1;
      recomputeLineDiscount(l);
      recompute(state);
    },
    // QV-4738 remove ONLY the item coupon; any manual discount stays (Android ivCoupon).
    removeItemCoupon(state, { payload }) {
      const l = state.lines.find((x) => x.orderId === payload);
      if (!l) return;
      l.couponDiscountName = "";
      l.couponDiscountType = "";
      l.couponDiscountValue = 0;
      l.couponDiscountFinalAmt = 0;
      l.couponMaxDiscount = 0;
      l.couponCanCombine = false;
      l.couponAltriaFunded = "0";
      l.couponPromotionCode = "";
      recomputeLineDiscount(l);
      if (l.manualDiscountAmt <= 0 && l.manualDiscountPercent <= 0 && toNum(l.bogoIdentifier) === 1) {
        l.bogoIdentifier = 0;
      }
      recompute(state);
    },
    // Editor-Cancel revert: restore a line's manual-discount + coupon snapshot wholesale.
    restoreLineDiscounts(state, { payload }) {
      const l = state.lines.find((x) => x.orderId === payload.orderId);
      if (!l) return;
      const s = payload.snapshot || {};
      l.manualDiscountAmt = toNum(s.manualDiscountAmt);
      l.manualDiscountPercent = toNum(s.manualDiscountPercent);
      l.couponDiscountName = String(s.couponDiscountName || "");
      l.couponDiscountType = String(s.couponDiscountType || "");
      l.couponDiscountValue = toNum(s.couponDiscountValue);
      l.couponMaxDiscount = toNum(s.couponMaxDiscount);
      l.couponCanCombine = !!s.couponCanCombine;
      l.couponAltriaFunded = String(s.couponAltriaFunded || "") === "1" ? "1" : "0";
      l.couponPromotionCode = l.couponAltriaFunded === "1" ? String(s.couponPromotionCode || "") : "";
      recomputeLineDiscount(l);
      recompute(state);
    },
    overridePrice(state, { payload }) {
      const l = state.lines.find((x) => x.orderId === payload.orderId);
      if (l) {
        // Android IPF:8806-8814 — the typed override is the CASH price; dual inflates it (gift cards never).
        l.manualOverrideBase = toNum(payload.price);
        l.manualOverridePrice = l.isGiftCard ? toNum(payload.price) : inflatePrice(state.dualPrice, payload.price);
        l.isBoGoProduct = false;
        l.bogoIdentifier = 1;
        l.isWarningShow = "";
        recompute(state);
      }
    },
    setLineTaxes(state, { payload }) {
      // Add/Remove Tax on a single line (line-item editor). taxList = the taxes now applied.
      const l = state.lines.find((x) => x.orderId === payload.orderId);
      if (l) {
        l.taxList = Array.isArray(payload.taxList) ? payload.taxList : [];
        if (toNum(l.bogoIdentifier) === 1) l.bogoIdentifier = 0; // Android resets bogoIdentifier on tax change
        recompute(state);
      }
    },
    setItemNote(state, { payload }) {
      const l = state.lines.find((x) => x.orderId === payload.orderId);
      if (l) l.note = String(payload.note == null ? "" : payload.note);
    },
    applyCoupon(state, { payload }) {
      // Android addSplitPaymentDiscountFlow (coupon branch) — the coupon becomes ONE more discount
      // line on top of whatever is already applied. The picker's min-amount gate (Android ~6315)
      // validates against the eligible non-dezi subtotal WITHOUT existing discounts; the VALUE is then
      // computed on the base MINUS every existing discount line (only when the cart has no
      // coupon-excluded items — with excluded items the eligible base is used as-is).
      const isPercent = payload.flag === SHOW_OFFER_PERCENTAGE;
      // Android picker (~6336): ANY coupon is refused while a % line exists — the check is not
      // conditional on the coupon's own type. (modal toasts "Percentage discount is already applied.")
      if (percentLine(state)) return;
      if (!Array.isArray(state.orderDiscounts)) state.orderDiscounts = [];
      const existing = sumAmt(dLines(state));
      const gateBase = payload.isOutdoor
        ? state.totals.subtotal - (state.totals.lotteryBuyAmount || 0)
        : listCouponBase(state, state.totals.subtotal, state.totals.lotteryBuyAmount);
      if (!payload.isOutdoor && toNum(payload.minAmount) > gateBase) return; // min not met → no-op (modal toasts)
      const upcs = deziSet(state);
      const eligible = couponEligibleSubtotal(state.lines);
      const base = payload.isOutdoor
        ? Math.max(state.totals.subtotal - existing - (state.totals.lotteryBuyAmount || 0) - deziNetSubtotal(state.lines, upcs, false), 0)
        : Math.max((hasCouponExcludedItems(state.lines) ? eligible : eligible - existing) - deziNetSubtotal(state.lines, upcs, true), 0);
      let amount;
      if (!isPercent) {
        amount = toNum(payload.discount);
        if (upcs.size > 0 && amount > base) amount = base; // Wave 6 cap, gated on dezi presence
      } else {
        amount = roundHalfUp(roundHalfUp(base) * (toNum(payload.discount) / 100));
        const cap = toNum(payload.maximumDiscount);
        if (cap !== 0 && amount > cap) amount = cap;
      }
      state.orderDiscounts.push({
        name: String(payload.name || "Coupon"),
        id: payload.id || "",
        altriaFunded: payload.altria_funded_discount ?? payload.altriaFundedDiscount ?? "",
        amount: roundHalfUp(amount),
        isPercent,
        isCoupon: true,
        isOtd: !!payload.isOutdoor,
      });
      // Android `mOrderDiscount = it` — the applied coupon (or the OTD pseudo-coupon) is the model.
      state.orderCouponModel = {
        flag: payload.flag,
        discount: payload.discount,
        minAmount: payload.minAmount,
        maximumDiscount: payload.maximumDiscount,
        name: payload.name,
        id: payload.id || "",
        isListCoupon: !payload.isOutdoor,
      };
      if (payload.isOutdoor) state.isOutdoor = true;
      recompute(state);
    },
    applyCustomDiscount(state, { payload }) {
      // Android addSplitPaymentDiscountFlow (custom branch): base = subtotal − EVERY existing discount
      // line − lottery buys − the manufacturer-discounted portion (QV-7124 rule 4). Not coupon-scoped:
      // a custom discount deliberately still applies across coupon-EXCLUDED categories. A custom $
      // stacks freely; a custom % is allowed only while no % line exists (isSplitPaymentDiscountHasPercentage).
      const isPercent = !!payload.isPercent;
      if (isPercent && percentLine(state)) return; // modal toasts "Percentage discount is already applied."
      if (!Array.isArray(state.orderDiscounts)) state.orderDiscounts = [];
      const upcs = deziSet(state);
      const sub = Math.max(
        state.totals.subtotal - sumAmt(dLines(state)) - (state.totals.lotteryBuyAmount || 0) - deziNetSubtotal(state.lines, upcs, false),
        0
      );
      let amount = isPercent ? roundHalfUp((toNum(payload.value) / 100) * sub) : toNum(payload.value);
      if (!isPercent && upcs.size > 0 && amount > sub) amount = sub; // Wave 6 cap, gated on dezi presence
      state.orderDiscounts.push({ name: "Discount", id: "", altriaFunded: "", amount: roundHalfUp(amount), isPercent, isCoupon: false, isOtd: false });
      // Android: only a custom % writes the synthetic model (flag "0", no min / max); a custom $
      // leaves mOrderDiscount untouched — so a $ coupon's min-amount re-check keeps running under it.
      if (isPercent) {
        state.orderCouponModel = { flag: SHOW_OFFER_PERCENTAGE, discount: String(payload.value), minAmount: "", maximumDiscount: "", name: "Discount", id: "", isListCoupon: false };
      }
      recompute(state);
    },
    // Android ivRemoveDiscount: deleteCoupon() + removeSplitDiscount(true) — removes EVERY order-level
    // discount (custom, coupon and OTD alike).
    clearOrderDiscount(state) {
      clearAllOrderDiscounts(state);
      recompute(state);
    },
    setOutdoor(state, { payload }) {
      state.isOutdoor = true;
      state.outDoorPrice = toNum(payload && payload.price);
      recompute(state);
    },
    setCustomer(state, { payload }) {
      state.customer = payload || null;
    },
    // Android orderCreateViewModel.setIdentificationNumber / setSpinnerData / setIdDateOfBirth /
    // setIdExpiryDate — one verification covers the whole order (gate = idNumber||idCardType set).
    setAgeVerified(state, { payload }) {
      state.isEighteenPlus = true;
      state.idNumber = (payload && payload.idNumber) || "";
      state.idCardType = (payload && payload.idCardType) || "";
      state.idDob = (payload && payload.dob) || "";
      state.idCardExDate = (payload && payload.idCardExDate) || "";
    },

    // ── Reductions (gift card / loyalty / store credit) — capped so the balance never goes negative ──
    applyGiftCard(state, { payload }) {
      // payload: { number, balance, amount }
      const number = String(payload.number || "").trim();
      if (!number || state.giftCards.some((g) => g.number === number)) return;
      const bal = toNum(payload.balance);
      // Android ATP:3300-3312: the redeem caps at customerOwes — the CARD total (mGrandTotal) in dual
      // price before any split, the remaining base once split (owner decision 10-01: copy Android).
      const dualCap = state.totals.dual && !isSplitState(state) ? toNum(state.totals.grandTotal) : state.totals.balanceDue;
      const use = Math.min(toNum(payload.amount), bal, dualCap);
      if (use <= 0) return;
      state.giftCards.push({ number, amountUsed: roundHalfUp(use), balance: roundHalfUp(bal) });
      recompute(state);
    },
    removeGiftCard(state, { payload }) {
      state.giftCards = state.giftCards.filter((g) => g.number !== payload);
      recompute(state);
    },
    applyLoyalty(state, { payload }) {
      // payload: { amount, dollarPerPoint } — points debited = amount / dollarPerPoint
      state.loyaltyAmount = 0;
      state.loyaltyPointsSpent = 0;
      recompute(state);
      const use = Math.min(toNum(payload.amount), state.totals.balanceDue);
      if (use <= 0) return;
      state.loyaltyAmount = roundHalfUp(use);
      const dpp = toNum(payload.dollarPerPoint);
      state.loyaltyPointsSpent = dpp > 0 ? roundHalfUp(use / dpp) : 0;
      recompute(state);
    },
    clearLoyalty(state) {
      state.loyaltyAmount = 0;
      state.loyaltyPointsSpent = 0;
      recompute(state);
    },
    applyStoreCredit(state, { payload }) {
      // payload: { amount }
      state.storeCreditAmount = 0;
      recompute(state);
      const use = Math.min(toNum(payload.amount), state.totals.balanceDue);
      if (use <= 0) return;
      state.storeCreditAmount = roundHalfUp(use);
      recompute(state);
    },
    clearStoreCredit(state) {
      state.storeCreditAmount = 0;
      recompute(state);
    },

    setNcaRemoved(state, { payload }) {
      state.ncaRemoved = !!payload;
    },
    setActiveOrderId(state, { payload }) {
      state.activeOrderId = payload || null;
    },

    // ── Payment tenders (cash / card / EBT) — each capped to the remaining balance due ──
    addPayment(state, { payload }) {
      // Android cashPayment with customerOwes < 0 (lottery/scratcher payout larger than the sale):
      // the ONE cash tender is recorded for mGrandTotal itself — a NEGATIVE amount, the cash handed
      // to the customer. Only cash may do this; every other tender is disabled for the transaction.
      if (state.totals.customerOwes < 0 && String(payload.payType).toLowerCase() === "cash" && toNum(payload.amount) < 0) {
        if ((state.payments || []).length > 0) return;
        state.payments.push({
          payType: "cash",
          amount: roundHalfUp(state.totals.customerOwes),
          tip: 0,
          ncaAmount: 0,
          ncaPercent: null,
          cashbackAmount: 0,
          cashbackFee: 0,
          paymentId: "",
          cardType: "",
          last4: "",
          paxDetails: "",
          paymentResult: "",
          cvvResult: "",
        });
        recompute(state);
        return;
      }
      const use = Math.min(toNum(payload.amount), state.totals.balanceDue);
      if (use <= 0) return;
      state.payments.push({
        // Android isSplitPayment: this tender left a remainder, or follows one ⇒ the order is a split.
        partial: roundHalfUp(state.totals.balanceDue - use) > 0 || (state.payments || []).length > 0,
        payType: payload.payType,
        amount: roundHalfUp(use), // base amount that pays down the balance
        tip: toNum(payload.tip),
        // Surcharge / cash-back are EXTRA charges on top of the base (not counted against balance).
        ncaAmount: toNum(payload.ncaAmount),
        ncaPercent: payload.ncaPercent != null ? payload.ncaPercent : null,
        cashbackAmount: toNum(payload.cashbackAmount),
        cashbackFee: toNum(payload.cashbackFee),
        paymentId: payload.paymentId || "",
        cardType: payload.cardType || "",
        last4: payload.last4 || "",
        paxDetails: payload.paxDetails || "",
        paymentResult: payload.paymentResult || "",
        cvvResult: payload.cvvResult || "",
      });
      recompute(state);
    },
    removePayment(state, { payload }) {
      state.payments.splice(payload, 1);
      recompute(state);
    },
    clearPayments(state) {
      state.payments = [];
      recompute(state);
    },

    resetCart(state) {
      const bogo = state.bogoList;
      const excl = state.couponExcludedCategoryIds;
      const dp = state.dualPrice;
      Object.assign(state, initialState, { bogoList: bogo, couponExcludedCategoryIds: excl, dualPrice: dp, lines: [] });
      recompute(state);
    },
    // Restore a snapshot — a held sale (pre-payment, no `payments`) OR a resumed in-progress order
    // (includes `payments`, the already-charged tenders). initialState resets first, so a held-sale
    // snapshot (no payments key) restores with an empty payments array.
    loadCart(state, { payload }) {
      // Deep-clone: the engine mutates lines in place, and a snapshot may be a frozen/shared object
      // (snapshots are plain JSON, so this is lossless).
      const snap = payload ? JSON.parse(JSON.stringify(payload)) : {};
      // exclude_coupons_store: the excluded-category list is CURRENT reference data, not part of a
      // snapshot — preserve it across the restore, then re-derive each line's eligibility (Android
      // recomputes eligibility from the live category list right before restore).
      const excl = state.couponExcludedCategoryIds;
      const dp = state.dualPrice;
      Object.assign(state, initialState, snap);
      state.couponExcludedCategoryIds = excl;
      state.dualPrice = dp; // the CURRENT store setting, never the snapshot's
      if (!Array.isArray(state.payments)) state.payments = [];
      // Back-fill PRE-stacking snapshots (single coupon XOR custom discount) into discount lines.
      if (!Array.isArray(snap.orderDiscounts)) {
        state.orderDiscounts = [];
        const isCoupon = toNum(snap.couponTotal) > 0;
        const cp = snap.coupon || {};
        const name = isCoupon ? (snap.isOutdoor ? "OTD" : cp.code || "Coupon") : "Discount";
        if (toNum(snap.orderPercentDiscount) > 0)
          state.orderDiscounts.push({ name, id: cp.id || "", altriaFunded: cp.altriaFunded || "", amount: toNum(snap.orderPercentDiscount), isPercent: true, isCoupon, isOtd: false });
        if (toNum(snap.orderAmountDiscount) > 0)
          state.orderDiscounts.push({ name, id: cp.id || "", altriaFunded: cp.altriaFunded || "", amount: toNum(snap.orderAmountDiscount), isPercent: false, isCoupon, isOtd: !!snap.isOutdoor });
        state.orderCouponModel = snap.couponModel ? { ...snap.couponModel, isListCoupon: true } : null;
      }
      // Back-fill lines from PRE-item-coupon snapshots (QV-4738): treat the legacy combined
      // orderDiscount/orderDiscountPercent as manual-only so the split model holds.
      for (const l of state.lines) {
        if (l.manualDiscountAmt === undefined) {
          l.manualDiscountAmt = toNum(l.orderDiscount);
          l.manualDiscountPercent = toNum(l.orderDiscountPercent);
        }
        if (l.couponDiscountName === undefined) {
          l.couponDiscountName = "";
          l.couponDiscountType = "";
          l.couponDiscountValue = 0;
          l.couponDiscountFinalAmt = 0;
          l.couponMaxDiscount = 0;
          l.couponCanCombine = false;
        }
        // Separate guard: a held sale from a build that already had item coupons but predates the
        // QV Altria item-coupon flag would skip the block above (couponDiscountName is defined).
        if (l.couponAltriaFunded === undefined) l.couponAltriaFunded = "0";
        if (l.couponPromotionCode === undefined) l.couponPromotionCode = "";
        // QV-7124 back-fill for pre-dezi snapshots. All three are DERIVED, so seeding them at zero is
        // correct and complete — the post-restore lookup re-injects from scratch. This is precisely
        // why the web cannot suffer Android's "held dezi discount applied twice on restore" bug.
        if (l.deziItemDiscountAmt === undefined) l.deziItemDiscountAmt = 0;
        if (l.deziBreakdown === undefined) l.deziBreakdown = [];
        if (l.isDeziUpcLine === undefined) l.isDeziUpcLine = false;
        if (l.isCommittedBogo === undefined) l.isCommittedBogo = false;
        if (l.isRestoredBogo === undefined) l.isRestoredBogo = false;
        if (l.isWarningShow === undefined) l.isWarningShow = "";
        if (l.variantUpc === undefined) l.variantUpc = "";
        // re-derive order-coupon eligibility from the CURRENT excluded-category list
        l.isCouponEligible = computeCouponEligibility((l.model || {}).cotegory, excl);
      }
      // A restored cart must look the manufacturer discount up fresh — the held result belongs to the
      // cart as it was, and mKonnekt amounts are conditional (they scale with quantity and can drop
      // out entirely below a threshold).
      state.deziDiscounts = null;
      const maxId = (snap.lines || []).reduce((m, l) => Math.max(m, Number(l.orderId) || 0), 0);
      if (maxId >= LINE_SEQ) LINE_SEQ = maxId + 1; // keep future line ids unique
      recompute(state);
    },
  },
});

export const {
  setDualPrice,
  setNcaRemoved,
  setBogoList,
  setDeziDiscounts,
  setCouponExcludedCategories,
  addItem,
  updateQty,
  expandLine,
  removeItem,
  addPayoutLine,
  setItemDiscount,
  applyItemCoupon,
  removeItemCoupon,
  restoreLineDiscounts,
  overridePrice,
  setLineTaxes,
  setItemNote,
  applyCoupon,
  applyCustomDiscount,
  clearOrderDiscount,
  setOutdoor,
  setCustomer,
  setAgeVerified,
  applyGiftCard,
  removeGiftCard,
  applyLoyalty,
  clearLoyalty,
  applyStoreCredit,
  clearStoreCredit,
  setActiveOrderId,
  addPayment,
  removePayment,
  clearPayments,
  loadCart,
  resetCart,
} = cartSlice.actions;

export default cartSlice.reducer;
